import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import checkchallanImage from "../../assets/challan-online.png";
import styles from "./Loan.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faUpload, faCreditCard, faPercent, faMoneyBillWave } from "@fortawesome/free-solid-svg-icons";
import LoanOfferComponent from "./LoanOfferComponent";

function Loan() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    vehicleNumber: "",
    phoneNumber: "",
    email: "",
    pincode: "",
    loanAmount: 300000,
    interestRate: 1.01,
    tenure: 120, // months
  });
  const [files, setFiles] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [emi, setEmi] = useState(3500);

  // Calculate EMI whenever loan parameters change
  useEffect(() => {
    calculateEMI();
  }, [formData.loanAmount, formData.interestRate, formData.tenure]);

  const calculateEMI = () => {
    const principal = formData.loanAmount;
    const ratePerMonth = formData.interestRate / 100;
    const tenure = formData.tenure;
    
    // EMI formula: [P x R x (1+R)^N]/[(1+R)^N-1]
    const emiAmount = (principal * ratePerMonth * Math.pow(1 + ratePerMonth, tenure)) / 
                      (Math.pow(1 + ratePerMonth, tenure) - 1);
                      
    setEmi(Math.round(emiAmount));
  };

const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "loanAmount" || name === "interestRate" || name === "tenure") {
      setFormData({ ...formData, [name]: Number(value) });
    } else if (name === "vehicleNumber") {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    if (!formData.vehicleNumber) {
      setError("Please enter your vehicle number");
      return false;
    }
    if (!formData.phoneNumber) {
      setError("Please enter your mobile number");
      return false;
    }
    if (!formData.pincode) {
      setError("Please enter your PIN code");
      return false;
    }
    return true;
  };

  const handleInitialSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowDocUpload(true);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const token = localStorage.getItem("token");
    const vehicle_id = localStorage.getItem("vehicle_id");

    if (!token) {
      setError("Authentication token missing. Please log in again.");
      return;
    }
    if (!vehicle_id) {
      setError("Vehicle ID missing. Please select a vehicle.");
      return;
    }

    setLoading(true);

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    const form = new FormData();
    form.append("service_type", "Loan");
    form.append("vehicle_id", vehicle_id);
    form.append(
      "additional_info",
      JSON.stringify({
        name: formData.name,
        phone_number: formData.phoneNumber,
        email: formData.email,
        pincode: formData.pincode,
        loanAmount: formData.loanAmount,
        interestRate: formData.interestRate,
        tenure: formData.tenure,
        emi: emi,
        loan: true,
      })
    );

    if (files.length > 0) {
      files.forEach((file) => {
        form.append("documents", file);
      });
    }

    try {
      const response = await fetch(
        "https://api.rtoxpert.com/api/service-requests",
        {
          method: "POST",
          headers,
          body: form,
          redirect: "follow",
        }
      );

      if (response.ok) {
        setShowPopup(true);
        setError(null);
        setTimeout(() => {
          setShowPopup(false);
          navigate("/profile");
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Connection error. Please check your internet connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        {/* Left Content */}
        <div className={styles.leftContent}>
          <h1 className={styles.title}>Get Instant Vehicle Loan</h1>

          {!showDocUpload ? (
            /* Initial Form */
            <div className={styles.formContainer}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Full Name"
                  className={styles.input}
                  value={formData.name}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="vehicleNumber"
                  placeholder="Enter Vehicle Number"
                  className={styles.input}
                  value={formData.vehicleNumber}
                  onChange={handleChange}
                  required
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  placeholder="Enter Mobile Number"
                  className={styles.input}
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  maxLength="10"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Enter Email Address"
                  className={styles.input}
                  value={formData.email}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="pincode"
                  placeholder="Enter PIN Code"
                  className={styles.input}
                  value={formData.pincode}
                  onChange={handleChange}
                  required
                  maxLength="6"
                />
                
                {/* Loan Amount Input */}
                <div className={styles.loanParameterContainer}>
                  <label htmlFor="loanAmount" className={styles.loanLabel}>Loan Amount (₹):</label>
                  <input
                    type="number"
                    id="loanAmount"
                    name="loanAmount"
                    placeholder="Loan Amount"
                    className={styles.input}
                    value={formData.loanAmount}
                    onChange={handleChange}
                    min="50000"
                    max="1000000"
                  />
                </div>
                
                {/* Loan Duration Input */}
                <div className={styles.loanParameterContainer}>
                  <label htmlFor="tenure" className={styles.loanLabel}>Loan Duration (months):</label>
                  <input
                    type="number"
                    id="tenure"
                    name="tenure"
                    placeholder="Loan Duration"
                    className={styles.input}
                    value={formData.tenure}
                    onChange={handleChange}
                    min="12"
                    max="240"
                  />
                </div>
                
                {/* Display Loan Summary */}
                <div className={styles.loanSummary}>
                  <h3 className={styles.summaryTitle}>Loan Summary</h3>
                  <div className={styles.summaryDetails}>
                    <div className={styles.summaryItem}>
                      <FontAwesomeIcon icon={faMoneyBillWave} className={styles.summaryIcon} />
                      <span className={styles.summaryLabel}>Amount:</span>
                      <span className={styles.summaryValue}>₹{formData.loanAmount.toLocaleString()}</span>
                    </div>
                    <div className={styles.summaryItem}>
                      <FontAwesomeIcon icon={faPercent} className={styles.summaryIcon} />
                      <span className={styles.summaryLabel}>Interest:</span>
                      <span className={styles.summaryValue}>{formData.interestRate}% p.m.</span>
                    </div>
                    <div className={styles.summaryItem}>
                      <FontAwesomeIcon icon={faCreditCard} className={styles.summaryIcon} />
                      <span className={styles.summaryLabel}>Monthly EMI:</span>
                      <span className={styles.summaryValue}>₹{emi.toLocaleString()} p.m.</span>
                    </div>
                  </div>
                </div>
                
                <button
                  className={styles.button}
                  onClick={handleInitialSubmit}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Apply Now"}
                </button>
                <p className={styles.terms}>
                  By continuing, I agree to the{" "}
                  <a href="/terms-conditions" className={styles.link}>
                    Terms of Use
                  </a>{" "}
                  &{" "}
                  <a href="/privacy-policy" className={styles.link}>
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          ) : (
            /* Document Upload Section */
            <div className={styles.docUploadContainer}>
              <h3 className={styles.uploadTitle}>Upload Required Documents</h3>

              <div className={styles.documentsList}>
                <div className={styles.documentsNeeded}>
                  <h4>Documents Required:</h4>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>Vehicle Registration Certificate (RC)</p>
                  </div>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>Income Proof (Salary Slips/Bank Statements)</p>
                  </div>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>ID Proof (Aadhar/PAN Card)</p>
                  </div>
                  <div className={styles.documentItem}>
                    <div className={styles.checkIcon}>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <p>Address Proof</p>
                  </div>
                </div>

                <div className={styles.fileUploadArea}>
                  <div className={styles.fileInputGroup}>
                    <div className={styles.fileUploadWrapper}>
                      <label className={styles.fileUploadLabel}>
                        <FontAwesomeIcon
                          icon={faUpload}
                          className={styles.uploadIcon}
                        />
                        Choose File(s)
                        <input
                          type="file"
                          className={styles.fileInput}
                          onChange={handleFileChange}
                          multiple
                        />
                      </label>
                    </div>

                    {files.length > 0 && (
                      <ul className={styles.uploadedFiles}>
                        {files.map((file, index) => (
                          <li key={index} className={styles.fileItem}>
                            <span>📄 {file.name}</span>
                            <button
                              className={styles.removeFileBtn}
                              onClick={() => removeFile(index)}
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className={styles.buttonGroup}>
                    <button
                      className={styles.backButton}
                      onClick={() => setShowDocUpload(false)}
                    >
                      Back
                    </button>
                    <button
                      className={styles.submitButton}
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!showDocUpload && (
            <div>
              <p className={styles.offerText}>
                Quick Approval • Competitive Rates • Minimum Documentation
              </p>
            </div>
          )}
        </div>

        {/* Right Content - Offer Details */}
        <div className={styles.rightContent}>
          <div className={styles.offerCard}>
            {/* Features */}
            <div className={styles.featuresContainer}>
              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </div>
                <span className={styles.featureText}>Quick Disbursal</span>
              </div>
              
              <div className={styles.divider}></div>
              
              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <FontAwesomeIcon icon={faPercent} />
                </div>
                <span className={styles.featureText}>Low Interest Rate</span>
              </div>
              
              <div className={styles.divider}></div>
              
              <div className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <span className={styles.featureText}>Zero Paperwork</span>
              </div>
            </div>
            
            {/* Exclusive offer section */}
            <div className={styles.exclusiveOffer}>
              <span>EXCLUSIVE OFFER</span>
            </div>
            
            <div className={styles.offerDetails}>
              <p className={styles.offerText}>Get instant cash in your bank account</p>
              <h2 className={styles.offerAmount}>₹{formData.loanAmount.toLocaleString()}</h2>
            </div>
            
            {/* EMI details */}
            <div className={styles.emiContainer}>
              <div className={styles.emiItem}>
                <div className={styles.emiLabel}>Monthly EMI</div>
                <div className={styles.emiValue}>
                  <span className={styles.emiAmount}>₹ {emi.toLocaleString()}</span>
                  <span className={styles.emiSuffix}>p.m</span>
                </div>
              </div>
              <div className={styles.emiItem}>
                <div className={styles.emiLabel}>Rate of Interest</div>
                <div className={styles.emiValue}>
                  <span className={styles.emiAmount}>{formData.interestRate}%</span>
                  <span className={styles.emiSuffix}>p.m</span>
                </div>
              </div>
            </div>
            
            {/* Button */}
            {/* <button 
              className={styles.withdrawButton}
              onClick={() => navigate("/Withdrawal")}
            >
              Withdraw Instant Money
            </button> */}
          </div>
        </div>
      </div>

      {error && <p className={styles.errorText}>{error}</p>}

      {showPopup && (
        <div className={styles.popup}>
          <p>
            Loan application submitted successfully! Our representative will contact you shortly. Redirecting to profile...
          </p>
        </div>
      )}

      <LoanOfferComponent />
    </div>
  );
}

export default Loan;